import { FC, useEffect, useState } from 'react'
import mclasses from '../../../../styles/modal.module.css'
import Modal from '@common/Modal'
import { useAsyncFn } from 'react-use'
import { crmApi, usersAPI } from '@/api'
import Select from '../../../base/Select/Select'
import { IcoRemove } from '@/utils/icons'
import Button from '@UI/New/Button'
import { useAddAlert } from '@/hooks/useAddAlert'
import { isError } from '@/helpers/checkQuery'
import type { LeadProfileLead } from '../../LeadProfileModal.types'

interface AddStudAccountModalProps {
  lead?: LeadProfileLead
  open: boolean
  onClose: () => void
  /** id привязанного студента — сразу обновляем карточку без ожидания getLead */
  onSuccess?: (userId: number) => void
}

export const SelectChildren: FC<{
  value: number | null
  onChange: (value: number | null) => void
  className?: string
}> = ({ value, onChange, className }) => {
  const [searchText, setSearchText] = useState('')
  const [usersState, doFetchUsers] = useAsyncFn(async () => {
    let filter: object = { 'filter[role][eq]': 'children', 'per-page': 200 }
    if (searchText.trim())
      filter = { 'filter[search]': searchText.trim(), ...filter }
    return await usersAPI.searchUsersForSelect({ ...filter })
  }, [searchText])

  const usersData = usersState.value?.data?.data || []

  useEffect(() => {
    doFetchUsers()
  }, [searchText, doFetchUsers])

  useEffect(() => {
    if (value === null) setSearchText('')
  }, [value])

  return (
    <Select
      showSearch
      loading={usersState.loading}
      options={usersData}
      onSearch={(text) => setSearchText(text)}
      value={value}
      onChange={onChange}
      placeholder="Введите ФИО, почту или номер телефона студента"
      className={className}
    />
  )
}

const AddStudAccountModal: FC<AddStudAccountModalProps> = ({
  lead,
  open,
  onClose,
  onSuccess,
}) => {
  const addAlert = useAddAlert()

  const [selectStudId, setSelectStudId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setSelectStudId(null)
      setSubmitting(false)
    }
  }, [open])

  const handleConfirm = async () => {
    if (!lead || !selectStudId) {
      addAlert({
        title: 'Ошибка',
        text: 'Не выбран личный кабинет студента или не указан лид',
        type: 'error',
      })
      return
    }

    setSubmitting(true)

    const resCheckUser = await crmApi.getLeadsByUserId({
      user_id: selectStudId,
    })
    if (isError(resCheckUser)) {
      addAlert({
        title: 'Ошибка',
        text: 'Ошибка проверки, есть ли у студента привязанный лид',
        type: 'error',
      })
      setSubmitting(false)
      return
    }

    if (resCheckUser.data.data.length > 0) {
      addAlert({
        title: 'Ошибка',
        // text: `Данный пользователь уже привязан к лиду.
        // ${resCheckUser.data.data
        //   .map((l) => `- ${getLeadFieldValue(l, 'NAME')}, id лида: ${l.id}`)
        //   .join('\n')}`,

        text: `Данный пользователь уже привязан к лиду, id лида: ${resCheckUser.data.data
          .map((l) => l.id)
          .join(', ')}`,
        type: 'error',
      })
      setSubmitting(false)
      return
    }

    crmApi
      .bindingStudAccount({
        lead_id: lead.id,
        user_id: selectStudId,
      })
      .then((res) => {
        if (isError(res)) throw new Error()
        addAlert({
          title: 'Успешно',
          text: `Успешно привязан личный кабинет студента`,
          type: 'success',
        })
        onSuccess?.(selectStudId)
      })
      .catch((error) => {
        addAlert({
          title: 'Ошибка',
          text: 'Ошибка привязки личного кабинета студента',
          type: 'error',
        })
        onClose()
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  return (
    <Modal
      active={open}
      className={mclasses.modalWrapper as any}
      setActive={() => onClose()}
    >
      <div className={mclasses.modalContainer}>
        <div className={mclasses.modalHeader}>
          <div className={mclasses.modalTitleContainer}>
            <span className={mclasses.modalHeaderTitle}>
              Привязать личный кабинет
            </span>
          </div>
          <div className={mclasses.closeIcon} onClick={() => onClose()}>
            <IcoRemove />
          </div>
        </div>
        <div className={mclasses.formWrapper}>
          <div>
            <p className={mclasses.label}>Выбрать личный кабинет студента</p>
            <SelectChildren value={selectStudId} onChange={setSelectStudId} />
          </div>
        </div>
        <div className={mclasses.actions}>
          <Button color="gray" onClick={() => onClose()}>
            Отменить
          </Button>
          <Button
            color="primary"
            onClick={() => handleConfirm()}
            isLoading={submitting}
            disabled={!selectStudId || submitting}
          >
            Подтвердить
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default AddStudAccountModal
